/**
 * Sense → Outcomes. Slice 60.
 *
 * The fetch, the frame, and the four writes handed to the screen. What the
 * screen asks for, and why, is argued in `OutcomesContent.tsx`; the website
 * postback card (slice 63) in `PostbackCard.tsx`.
 *
 * Slice 65 gave every Sense screen the same band at the top: the eyebrow
 * "Sense" with the pillar's glyph, and one line on what this screen answers.
 * Since the split into Reach, Connect and Sense, a person arriving here from a
 * link on the Overview or a campaign page needs to be told which part of Wend
 * they are in; the sidebar says it too, but only to someone who looks there.
 * The band sits here in the Page wrapper and not in the Content component
 * that `verify-sense-screens` renders, so those renders stay the screen's
 * substance and do not start counting a header's words. The name and glyph
 * come from `lib/pillars.tsx`, the one place a pillar is named.
 */
import { PageHeader } from '@wend/ui';

import { AppLayout } from '../../components/AppLayout';
import { ErrorState } from '../../components/ErrorState';
import { API_BASE_URL } from '../../lib/config';
import {
  createOutcomeType,
  createPostbackKey,
  fetchOutcomes,
  fetchPostbackKey,
  recordDeclaredOutcome,
  updateOutcomeType,
  uploadOutcomes,
} from '../../lib/api/sense';
import { PILLAR_IDENTITY } from '../../lib/pillars';
import { useAsyncData } from '../../lib/use-async-data';
import { OutcomesContent, type OutcomeActions } from './OutcomesContent';
import { PostbackCard } from './PostbackCard';

const ACTIONS: OutcomeActions = {
  createType: createOutcomeType,
  updateType: updateOutcomeType,
  upload: uploadOutcomes,
  declare: recordDeclaredOutcome,
};

export function OutcomesPage() {
  const { data, error, status, reload } = useAsyncData((signal) => fetchOutcomes(signal), []);
  // Slice 63: the website postback key. Its own read: a failure here must not
  // take the rest of the page down, and the card simply does not draw.
  const postback = useAsyncData((signal) => fetchPostbackKey(signal), []);

  return (
    <AppLayout title="Outcomes">
      <PageHeader
        eyebrow={PILLAR_IDENTITY.sense.label}
        icon={PILLAR_IDENTITY.sense.icon}
        lede="What counts as a conversion here, and the conversions you record."
      />
      <div className="flex flex-col gap-6" data-role="outcomes-page">
        {status === 'error' && data === null && (
          <ErrorState
            error={error}
            title="Could not load outcomes"
            onRetry={reload}
            className="flex flex-col items-start gap-3 rounded-base border border-line px-4 py-5"
          />
        )}
        {data !== null && <OutcomesContent overview={data} actions={ACTIONS} onChange={reload} />}
        {/* Only once a conversion type exists: a postback names one. */}
        {data !== null && data.types.length > 0 && postback.data !== null && (
          <PostbackCard
            status={postback.data}
            endpoint={`${API_BASE_URL}/public/conversions`}
            types={data.types}
            onCreate={createPostbackKey}
          />
        )}
        {data !== null && data.types.length > 0 && postback.status === 'error' && postback.data === null && (
          <p className="text-[0.85rem] text-muted" data-role="postback-unavailable">
            Website conversions could not be loaded.
          </p>
        )}
      </div>
    </AppLayout>
  );
}
